/* eslint-disable @typescript-eslint/naming-convention -- keys are external Datadog action names (spaces/dots), not identifiers */

// Deliberate: this `scope:shared` lib types its payloads against a `scope:tool-library` generated
// contract rather than duplicating the enum. Referencing the generated DTO is what CLAUDE.md's
// API-alias rule asks for, and the catalog already carries tool-library-only action names. If more
// domains start emitting events here, retag the lib rather than widening these to `string`.
import type { ToolDataModuleDataContractsToolDataSource as ToolDataSource } from '@tfs/tool-library-data-api';

/**
 * Central catalog of every Datadog RUM custom action Toolhive emits.
 *
 * Each key is the action name passed to `datadogRum.addAction`; its value is the
 * payload shape. `RumAnalyticsService.trackAction` is typed against this map, so
 * action names and payloads are checked at compile time — no stringly-typed drift.
 *
 * Naming convention for new events: `domain.entity.action` (dotted, lower_snake
 * segments). The two `search … Endpoint` names predate this and are kept verbatim
 * so existing Datadog dashboards keep matching.
 */
export interface RumEventMap {
  // — Existing search-endpoint actions (names kept verbatim) —
  'search Tool Item Endpoint': {
    requestDuration: number;
    toolsHit: number;
    totalToolCount: number;
    toolItemSearchUrl: string;
    manufacturers: string[];
    manufacturerNames: string;
    manufacturerCount: number;
  };
  'search Assembly Endpoint': {
    requestDuration: number;
    assemblyHit: number;
    totalAssemblyCount: number;
    assemblySearchUrl: string;
  };

  // — Pure interaction events (no backend equivalent) —
  'marketplace.supplier.click': { supplierName: string };
  'cam_plugin.download': { pluginName: string; camModule: string };

  // — Bulk-operation outcome events (engagement signal; authoritative counts live in backend OTel) —
  'tool_item.bulk_add': { itemCount: number; success: boolean };
  'tool_item.bulk_delete': { selectedCount: number; deletedCount: number; failedCount: number };

  // — Manual-tool engagement. A button press, not a navigation, so RUM's automatic view
  //   tracking captures nothing about it. Pairs with `item_source` on the view context below
  //   to answer "are manually created tools actually used?".
  //
  //   `source` duplicates the `item_source` the SDK merges in from the active view context
  //   (`domain/assembly.ts`). Kept deliberately so the action is queryable on its own attributes
  //   instead of depending on that merge, which would silently drop the dimension if this action
  //   ever fires from somewhere that isn't a described detail view. —
  'tool_item.assembly.start': { source: ToolDataSource };
}

/** Any action name in the catalog. */
export type RumEventName = keyof RumEventMap;

/**
 * Names for the detail views, replacing their URLs in RUM.
 *
 * Detail routes embed a UUID, so every opened item would otherwise be its own high-cardinality
 * `@view.url_path` and "detail pages opened" could not be charted as one number.
 *
 * These mirror route paths in `libs/tool-library/shell/src/lib/lib.routes.ts`, which is the source
 * of truth — nothing ties them together at compile time, so update both together if a route moves.
 */
export const RUM_VIEW_NAMES = {
  itemDetails: '/library/items/my-inventory/:id',
  assemblyDetails: '/library/assemblies/:id',
} as const;

/** Any view name in the catalog. */
export type RumViewName = (typeof RUM_VIEW_NAMES)[keyof typeof RUM_VIEW_NAMES];

/**
 * Attributes attached to the *current* RUM view, surfacing as `@view.context.*`.
 *
 * RUM already emits a view event per route change, so detail-page opens need no custom action —
 * what it cannot know is the semantics behind the UUID in the URL. Enriching the existing view
 * keeps one countable stream: `@type:view @view.context.item_source:Manual`.
 *
 * Keys are snake_case to match Datadog's own attribute conventions. Declared as a type alias, not
 * an interface, so it satisfies the SDK's `Context` index signature without us having to widen the
 * shape to `[key: string]: unknown`.
 */
export type RumViewContext = {
  item_source: ToolDataSource;
  manufacturer?: string;
  tool_class?: string;
};
